import Plugin from '../core/Plugin';

/**
 * NoteBoxPlugin - Adds a toolbar button to wrap selected text in a note box accordion
 */
class NoteBoxPlugin extends Plugin {
  constructor(editor, options = {}) {
    super(editor, options);
    this.name = 'NoteBox';
  }

  executeCommand(command) {
    if (command === 'insertNoteBox' || command === 'noteBox') {
      this.insertNoteBox();
    }
  }

  handleKeyboard(event) {
    // Get the active element
    const activeElement = document.activeElement;

    // Check if we're inside a note box content area
    const noteBoxContent =
      activeElement?.closest('.note-content') ||
      (activeElement?.classList?.contains('note-content') ? activeElement : null);

    if (!noteBoxContent) {
      return false; // Not in a note box, don't handle
    }

    // Handle ESC key to exit note box
    if (event.key === 'Escape') {
      event.preventDefault();
      this.exitNoteBox(noteBoxContent);
      return true;
    }

    // Handle backspace at the start of note box content
    if (event.key === 'Backspace') {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);

        // Check if we're at the very start of the note box content
        if (range.collapsed && this.isAtStartOfNoteBox(noteBoxContent, range)) {
          // If the note box is empty or only has a BR, delete the entire note box
          if (this.isNoteBoxEmpty(noteBoxContent)) {
            event.preventDefault();
            this.deleteNoteBox(noteBoxContent);
            return true;
          }
          // Otherwise, let the normal backspace work within the content
          // (no preventDefault needed - we're just checking the condition)
        }
      }
    }

    return false; // Event not handled
  }

  isAtStartOfNoteBox(noteBoxContent, range) {
    // Check if cursor is at the very beginning of the note box content
    const startNode = range.startContainer;
    const startOffset = range.startOffset;

    // If we're at offset 0 and this is the first text node or at the beginning
    if (startOffset === 0) {
      // Walk up the tree to see if this is the first content
      let node = startNode;
      while (node && node !== noteBoxContent) {
        // Check if there's any previous sibling with content
        let prev = node.previousSibling;
        while (prev) {
          if (prev.nodeType === Node.TEXT_NODE && prev.textContent.trim()) {
            return false; // There's text before us
          }
          if (prev.nodeType === Node.ELEMENT_NODE && prev.tagName !== 'BR') {
            return false; // There's an element before us
          }
          prev = prev.previousSibling;
        }
        node = node.parentNode;
      }
      return true;
    }

    return false;
  }

  isNoteBoxEmpty(noteBoxContent) {
    // Check if note box only contains empty paragraphs or BR tags
    const text = noteBoxContent.textContent.trim();
    if (text.length > 0) {
      return false;
    }

    // Also check if there are any meaningful elements (images, tables, etc.)
    const meaningfulElements = noteBoxContent.querySelectorAll('img, table, iframe, video');
    return meaningfulElements.length === 0;
  }

  deleteNoteBox(noteBoxContent) {
    // Find the parent note box accordion
    const noteBox = noteBoxContent.closest('[data-notebox="true"]');
    if (!noteBox) {
      return;
    }

    // Get the next sibling to focus after deletion
    let nextElement = noteBox.nextSibling;
    while (
      nextElement &&
      nextElement.nodeType === Node.TEXT_NODE &&
      !nextElement.textContent.trim()
    ) {
      nextElement = nextElement.nextSibling;
    }

    // If no next element, try previous
    if (!nextElement) {
      nextElement = noteBox.previousSibling;
      while (
        nextElement &&
        nextElement.nodeType === Node.TEXT_NODE &&
        !nextElement.textContent.trim()
      ) {
        nextElement = nextElement.previousSibling;
      }
    }

    // Remove the note box
    noteBox.remove();

    // Focus the next/previous element or the editor itself
    if (nextElement && nextElement.nodeType === Node.ELEMENT_NODE) {
      // Try to place cursor in the element
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(nextElement);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    } else {
      // Focus the editor
      this.editor.richEditor.focus({ preventScroll: true });
    }

    // Sync and save
    this.editor.syncToTextarea();
    this.editor.saveToHistory();
  }

  exitNoteBox(noteBoxContent) {
    // Find the parent note box accordion
    const noteBox = noteBoxContent.closest('[data-notebox="true"]');
    if (!noteBox) {
      return;
    }

    // Find the next element after the note box
    let nextElement = noteBox.nextSibling;
    while (
      nextElement &&
      nextElement.nodeType === Node.TEXT_NODE &&
      !nextElement.textContent.trim()
    ) {
      nextElement = nextElement.nextSibling;
    }

    // If no next element, create one
    if (!nextElement) {
      const p = document.createElement('p');
      p.innerHTML = '<br>';
      noteBox.parentNode.insertBefore(p, noteBox.nextSibling);
      nextElement = p;
    }

    // Focus the next element
    if (nextElement && nextElement.nodeType === Node.ELEMENT_NODE) {
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(nextElement);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }

  insertNoteBox() {
    // Focus the editor before inserting
    if (this.editor.richEditor) {
      this.editor.richEditor.focus({ preventScroll: true });
    }

    // Get the current selection
    const selection = window.getSelection();
    let selectedContent = '';

    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      // Only extract content if there's an actual selection (not just a cursor)
      if (!range.collapsed) {
        // Extract the selected content as HTML (preserving structure)
        const container = document.createElement('div');
        container.appendChild(range.cloneContents());
        selectedContent = container.innerHTML;

        // Note: We don't call deleteContents() here because insertHTML() will handle it
        // Calling it twice causes issues with text deletion
      }
    }

    // Create the note box accordion structure
    const details = document.createElement('details');
    details.style.setProperty('display', 'block', 'important');
    details.className =
      'demme-note-box-accordion collapse collapse-plus bg-demme-orange-50 rounded-xl mb-4';
    details.setAttribute('data-notebox', 'true');

    // Create the summary (header)
    const summary = document.createElement('summary');
    summary.className =
      'collapse-title text-xl font-normal text-demme-orange-700 flex items-center gap-2';
    summary.style.fontFamily = '\'Bree Serif\', serif';

    const icon = document.createElement('i');
    icon.className = 'fal fa-notes';
    icon.setAttribute('aria-hidden', 'true');

    // Create a span for the Note label and style it for left alignment
    const noteLabel = document.createElement('span');
    noteLabel.textContent = 'Note';
    noteLabel.className = 'note-label';
    noteLabel.style.display = 'inline-block';
    noteLabel.style.textAlign = 'left';
    noteLabel.style.marginRight = '8px';

    summary.appendChild(noteLabel);
    summary.appendChild(icon);

    details.appendChild(summary);

    // Create the content wrapper structure
    const spaWrapper = document.createElement('div');
    spaWrapper.className = 'spa_content_wrapper';

    const collapseContent = document.createElement('div');
    collapseContent.className = 'collapse-content';

    const noteContent = document.createElement('div');
    noteContent.className = 'note-content pb-0';
    noteContent.contentEditable = 'true';

    // Add the selected content or placeholder
    if (selectedContent) {
      noteContent.innerHTML = selectedContent;
    } else {
      // Add a placeholder paragraph
      const p = document.createElement('p');
      p.innerHTML = '<br>';
      noteContent.appendChild(p);
    }

    collapseContent.appendChild(noteContent);
    spaWrapper.appendChild(collapseContent);
    details.appendChild(spaWrapper);

    // Insert the accordion
    this.editor.insertHTML(details.outerHTML);

    // Ensure there's content after the note box to click on
    // This allows users to click out of the note box on empty pages
    this.ensureContentAfterNoteBox();

    // Focus the note box content area for immediate editing
    this.focusNoteBoxContent();

    // Sync and save to history
    this.editor.syncToTextarea();
    this.editor.saveToHistory();
  }

  isRichEditorReady() {
    // Check if richEditor exists and has querySelectorAll method
    return this.editor.richEditor && typeof this.editor.richEditor.querySelectorAll === 'function';
  }

  focusNoteBoxContent() {
    if (!this.isRichEditorReady()) {
      return;
    }

    // Find the last inserted note box (should be the one we just created)
    const noteBoxes = this.editor.richEditor.querySelectorAll('[data-notebox="true"]');
    if (noteBoxes.length === 0) {
      return;
    }

    const lastNoteBox = noteBoxes[noteBoxes.length - 1];
    const noteContent = lastNoteBox.querySelector('.note-content');

    if (noteContent) {
      // Focus the content area
      noteContent.focus();

      // Place cursor at the start of the content
      const range = document.createRange();
      const selection = window.getSelection();
      range.selectNodeContents(noteContent);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }

  ensureContentAfterNoteBox() {
    if (!this.isRichEditorReady()) {
      return;
    }

    // Find all note boxes in the editor
    const noteBoxes = this.editor.richEditor.querySelectorAll('[data-notebox="true"]');

    noteBoxes.forEach((noteBox) => {
      // Check if there's a next sibling
      let nextSibling = noteBox.nextSibling;

      // Skip over empty text nodes
      while (
        nextSibling &&
        nextSibling.nodeType === Node.TEXT_NODE &&
        !nextSibling.textContent.trim()
      ) {
        nextSibling = nextSibling.nextSibling;
      }

      // If no next sibling or next sibling is also a note box, add a paragraph
      if (
        !nextSibling ||
        (nextSibling.nodeType === Node.ELEMENT_NODE && nextSibling.getAttribute('data-notebox'))
      ) {
        const p = document.createElement('p');
        p.innerHTML = '<br>';
        noteBox.parentNode.insertBefore(p, noteBox.nextSibling);
      }
    });
  }

  getMetadata() {
    return {
      name: 'Note Box',
      version: '1.0.0',
      description: 'Adds a toolbar button to wrap selected text in a note box accordion',
      author: 'AtlaraFirm',
    };
  }
}

export default NoteBoxPlugin;
